import React, { useEffect, useRef } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useWebSocketDeviceData } from '../hooks/useWebSocketDeviceData'
import { OCAColors } from '../theme/colors'
import { TabBar } from './ui/TabBar'
import { DashboardPage } from './pages/DashboardPage'
import { ZonesPage } from './pages/ZonesPage'
import { DevicesPage } from './pages/DevicesPage'
import { AlarmsPage } from './pages/AlarmsPage'
import { ProfilePage } from './pages/ProfilePage'

// Map routes to their components for dual-screen transitions
const routeComponents: Record<string, React.ComponentType> = {
  '/dashboard': DashboardPage,
  '/zones': ZonesPage,
  '/devices': DevicesPage,
  '/alarms': AlarmsPage,
  '/settings': ProfilePage,
  '/profile': ProfilePage,
}

export const Layout: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Initialize WebSocket connection at layout level so it persists across all pages
  console.log('🏗️ Layout component - initializing WebSocket connection')
  const { isConnected: wsConnected, error: wsError } = useWebSocketDeviceData()
  console.log('🏗️ Layout - WebSocket status:', { wsConnected, wsError })

  const [touchStart, setTouchStart] = React.useState<{ x: number; y: number } | null>(null)
  const [isTransitioning, setIsTransitioning] = React.useState(false)
  const [slideDirection, setSlideDirection] = React.useState<'left' | 'right' | null>(null)
  const [swipeDetected, setSwipeDetected] = React.useState(false)
  const prevLocationRef = useRef(location.pathname)
  const [displayLocation, setDisplayLocation] = React.useState(location)
  const [transitionStage, setTransitionStage] = React.useState<'idle' | 'transitioning' | 'exiting'>('idle')

  // Define tab order for swipe navigation
  const tabs = ['/dashboard', '/zones', '/devices', '/alarms', '/settings']
  const currentTabIndex = tabs.indexOf(location.pathname)

  // Minimum swipe distance (in px) to trigger navigation
  const minSwipeDistance = 75

  // Detect route changes and set animation direction
  useEffect(() => {
    const prevIndex = tabs.indexOf(prevLocationRef.current)
    const currentIndex = tabs.indexOf(location.pathname)

    if (prevIndex !== -1 && currentIndex !== -1 && prevIndex !== currentIndex) {
      const direction = currentIndex > prevIndex ? 'left' : 'right'
      setSlideDirection(direction)

      // Step 1: Position both screens without animation
      setTransitionStage('transitioning')
      setIsTransitioning(false)

      // Step 2: Use requestAnimationFrame for better iOS compatibility
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(true)
        })
      })

      // After transition completes, update display location and clean up
      setTimeout(() => {
        setTransitionStage('exiting')
        setDisplayLocation(location)

        // Small delay to ensure new content is rendered before cleaning up
        setTimeout(() => {
          setSlideDirection(null)
          setIsTransitioning(false)
          setTransitionStage('idle')
        }, 50)
      }, 466) // 450ms transition + 16ms initial delay
    }

    prevLocationRef.current = location.pathname
  }, [location.pathname, tabs])

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    })
    setSwipeDetected(false)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || swipeDetected || isTransitioning) return

    const currentTouch = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    }

    const deltaX = currentTouch.x - touchStart.x
    const deltaY = Math.abs(currentTouch.y - touchStart.y)
    const absDeltaX = Math.abs(deltaX)

    // If there's any noticeable horizontal movement, check if it's primarily horizontal
    if (absDeltaX > 5 || deltaY > 5) {
      // If horizontal movement is greater than vertical, prevent default to stop scrolling
      if (absDeltaX > deltaY) {
        e.preventDefault()

        // Check if swipe threshold is met to trigger navigation
        const isLeftSwipe = deltaX < -minSwipeDistance
        const isRightSwipe = deltaX > minSwipeDistance

        if (isLeftSwipe && currentTabIndex < tabs.length - 1) {
          setSwipeDetected(true)
          setIsTransitioning(true)
          navigate(tabs[currentTabIndex + 1])
        } else if (isRightSwipe && currentTabIndex > 0) {
          setSwipeDetected(true)
          setIsTransitioning(true)
          navigate(tabs[currentTabIndex - 1])
        }
      }
    }
  }

  const onTouchEnd = () => {
    setTouchStart(null)
    setSwipeDetected(false)
  }

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: OCAColors.background,
      }}>
        <div>Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    navigate('/login')
    return null
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: OCAColors.gray900,
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {/* Main content area */}
      <main
        style={{
          flex: 1,
          overflow: 'hidden',
          padding: 0,
          minHeight: 0,
          maxHeight: '100vh',
          backgroundColor: '#F2F2F7',
          position: 'relative',
          touchAction: 'pan-y', // Allow vertical scrolling only
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Previous screen (sliding out) */}
        {transitionStage === 'transitioning' && (() => {
          const PreviousComponent = routeComponents[displayLocation.pathname]
          return PreviousComponent ? (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                overflow: 'hidden', // Lock scrolling during transition
                backgroundColor: '#F2F2F7',
                transform: isTransitioning && slideDirection
                  ? `translate3d(${slideDirection === 'left' ? '-100%' : '100%'}, 0, 0)` // Final position: off-screen
                  : 'translate3d(0, 0, 0)', // Initial position: center
                transition: isTransitioning
                  ? 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)'
                  : 'none',
                willChange: 'transform',
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden',
                zIndex: 1,
              }}
            >
              <PreviousComponent />
            </div>
          ) : null
        })()}

        {/* Current screen (sliding in) */}
        <div
          style={{
            position: transitionStage === 'transitioning' ? 'absolute' : 'relative',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: transitionStage === 'transitioning' ? 'hidden' : 'auto', // Lock scrolling during transition
            backgroundColor: '#F2F2F7',
            transform: transitionStage === 'transitioning'
              ? isTransitioning
                ? 'translate3d(0, 0, 0)' // Final position: center
                : slideDirection === 'left'
                  ? 'translate3d(100%, 0, 0)' // Initial position: right side (before animation)
                  : 'translate3d(-100%, 0, 0)' // Initial position: left side (before animation)
              : 'translate3d(0, 0, 0)',
            transition: isTransitioning
              ? 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)'
              : 'none',
            willChange: 'transform',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            zIndex: transitionStage === 'transitioning' ? 2 : 'auto',
          }}
        >
          <Outlet />
        </div>
      </main>

      {/* iOS-style Tab Bar */}
      <TabBar />
    </div>
  )
}